/**
 * usePlanAutosave — debounce-save del PlanDoc al backend.
 *
 * Cuando `dirty === true`, programa un guardado a 1500ms. Si llegan más
 * cambios el timer se reinicia. Devuelve `{ saving, lastSavedAt, save }`
 * para que el panel lateral muestre estado y permita Save manual.
 *
 * Mapeo PlanDoc → backend (`PUT /aetherlite/plans/:id`):
 *   PlanDoc.name      → name
 *   PlanDoc.template  → template
 *   PlanDoc.sections  → sections (JSONB list directa, NO envuelta)
 *   PlanDoc.brand     → brand (JSONB — estilo del plan, Feature 3)
 *   client name       → client_name (denormalizado desde cover/client section)
 *
 * `brand` se persiste desde Feature 3 para que el estilo del plan (paleta,
 * tipografía, densidad, tema) sobreviva un refresh. Los timestamps los maneja
 * Postgres y no se envían.
 *
 * `client_id` NO se toca en el autosave: ese vínculo se gestiona desde la UI
 * (chip "Cliente" del topbar) con un updatePlan explícito.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { updatePlan } from "../../api/plans";
import { useToast } from "../../lib/toast";
import type { CoverData, PlanDoc, PlanSection } from "../../types/plan";

interface UsePlanAutosaveResult {
  saving: boolean;
  lastSavedAt: Date | null;
  /** Forzar guardado inmediato (skip debounce). */
  save: () => Promise<void>;
  /** Mensaje de error de la última escritura, si la hubo. */
  error: string | null;
}

export function usePlanAutosave(
  plan: PlanDoc | null,
  dirty: boolean,
  /** id de DB del plan (no el id local del PlanDoc). */
  dbId: number | null,
  onSaved?: () => void,
): UsePlanAutosaveResult {
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const planRef = useRef<PlanDoc | null>(plan);
  const timer = useRef<number | null>(null);
  const onSavedRef = useRef(onSaved);

  // Mantener refs frescos (evita stale closures sin re-crear el timer).
  useEffect(() => {
    planRef.current = plan;
  }, [plan]);
  useEffect(() => {
    onSavedRef.current = onSaved;
  }, [onSaved]);

  const doSave = useCallback(async (): Promise<void> => {
    const p = planRef.current;
    if (!p || !dbId) return;
    setSaving(true);
    setError(null);
    try {
      const cover = p.sections.find((s) => s.type === "cover");
      const clientName = (cover?.data as CoverData | undefined)?.clientName || extractClientName(p.sections);
      await updatePlan(dbId, {
        name: p.name,
        template: p.template,
        client_name: clientName || null,
        sections: p.sections,
        brand: p.brand,
      });
      setLastSavedAt(new Date());
      onSavedRef.current?.();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
      toast("Error al guardar: " + msg);
    } finally {
      setSaving(false);
    }
  }, [dbId, toast]);

  // Debounce
  useEffect(() => {
    if (!dirty || !dbId) return;
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      doSave();
    }, 1500);
    return () => {
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, [dirty, doSave, dbId, plan]);

  // Flush en unmount: si quedaba algo pendiente, salvar.
  useEffect(() => {
    return () => {
      if (timer.current) {
        window.clearTimeout(timer.current);
        // Best-effort, no esperamos.
        if (dbId && planRef.current) doSave();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { saving, lastSavedAt, save: doSave, error };
}

function extractClientName(sections: PlanSection[]): string {
  for (const s of sections) {
    if (s.type === "client") return (s.data as any).fullName || "";
  }
  return "";
}
